<template>
  <div class="time-entries text-center">
    <TopMenu
      :back-to="'/dashboard/time-entries/' + $route.params.id"
      :title="expenseTitle"
      dark-background/>
    <div class="container-fluid content">
      <FormSelect
        :disabled="selectedTimeEntry.isSubmitted"
        :value="localExpense.expenseType.id"
        :options="formatToOptions(availableExpenses, 'name', 'id')"
        :name="$t('TIME_ENTRIES.EXPENSE.EXPENSE_TYPE_LABEL')"
        :placeholder="$t('TIME_ENTRIES.EXPENSE.EXPENSE_TYPE_LABEL')"
        class="row-item-transparent-with-border pb-2"
        validate="required"
        number
        @input="input"/>
      <FormInputNumber
        v-validate="'required'"
        :disabled="selectedTimeEntry.isSubmitted"
        :prepend="localExpense.expenseType.unit"
        :precision="precision"
        v-model="localExpense.qty"
        :name="$t('TIME_ENTRIES.EXPENSE.QUANTITY_LABEL')"
        :placeholder="$t('TIME_ENTRIES.EXPENSE.QUANTITY_LABEL')"
        class="row-item-transparent-with-border"
        align="right"/>
      <h4 class="mt-3">{{ $t('TIME_ENTRIES.EXPENSE.INVOICE') }}</h4>
      <div
        v-if="!isAdd"
        class="invoice-content">
        <PhotoContainer
          v-if="localExpense && localExpense.attachedFiles.length > 0"
          :src="selectedAttachedFileUrl"
          :show="selectedAttachedFile?true:false"
          @click="goToPhoto(selectedAttachedFile.id)" />
        <ActionButton
          v-if="localExpense.attachedFiles.length <= 0 && !selectedTimeEntry.isSubmitted"
          class="btn btn-lg btn-primary btn-block mt-3 mb-3"
          @click="goToPhoto('add')">{{ $t("PHOTO.ATTACH_PHOTO_BUTTON") }}</ActionButton>
        <span v-if="localExpense.attachedFiles.length <= 0 && selectedTimeEntry.isSubmitted">
          {{ $t("PHOTO.NO_PHOTO") }}
        </span>
      </div>
      <p v-if="isAdd">{{ $t('TIME_ENTRIES.EXPENSE.INVOICE_WARNING') }}</p>
      <h4 class="mt-3">Notes</h4>
      <FormTextArea
        :disabled="selectedTimeEntry.isSubmitted"
        v-model="localExpense.note"
        :placeholder="$t('TIME_ENTRIES.EXPENSE.NOTE_LABEL')"/>
      <ActionButton
        v-if="isAdd"
        class="btn btn-lg btn-primary btn-block mt-3 mb-3"
        @click="add">{{ $t("TIME_ENTRIES.EXPENSE.ADD_BUTTON") }}</ActionButton>
      <ActionButton
        v-if="!isAdd && !selectedTimeEntry.isSubmitted"
        class="btn btn-lg btn-primary btn-block mt-3 mb-3"
        @click="edit">{{ $t("TIME_ENTRIES.EXPENSE.EDIT_BUTTON") }}</ActionButton>
      <ActionButton
        v-if="!isAdd && !selectedTimeEntry.isSubmitted"
        class="btn btn-lg btn-danger btn-block mt-3 mb-3"
        @click="showConfirmDelete = true">{{ $t("TIME_ENTRIES.EXPENSE.DELETE_BUTTON") }}</ActionButton>
    </div>
    <BottomMenu />
    <ConfirmModal
      v-if="showConfirmDelete"
      show-modal
      size="md"
      @cancel="showConfirmDelete = false"
      @confirm="confirmDelete">{{ $t("TIME_ENTRIES.EXPENSE.CONFIRM_MODAL_DELETE_EXPENSE") }}</ConfirmModal>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import cloneDeep from 'lodash/cloneDeep';
import TopMenu from '@/components/Menu/TopMenu';
import BottomMenu from '@/components/Menu/BottomMenu';
import ActionButton from '@/components/Actions/ActionButton';
import ConfirmModal from '@/components/Modal/ConfirmModal';
import FormSelect from '@/components/Forms/FormSelect';
import FormInput from '@/components/Forms/FormInput';
import FormInputNumber from '@/components/Forms/FormInputNumber';
import FormTextArea from '@/components/Forms/FormTextArea';
import PhotoContainer from '@/components/Utils/PhotoContainer';
import UrlHelper from '@/helpers/url-helper';

export default {
  name: 'Expense',
  components: {
    ActionButton,
    TopMenu,
    BottomMenu,
    ConfirmModal,
    FormSelect,
    FormInput,
    FormInputNumber,
    FormTextArea,
    PhotoContainer,
  },
  mixins: [UrlHelper],
  data() {
    return {
      localExpense: null,
      showConfirmDelete: false,
    };
  },
  computed: {
    ...mapState('expense', ['availableExpenses', 'selectedExpense']),
    ...mapState('timeEntry', ['selectedTimeEntry']),
    ...mapState('attachedFile', ['selectedAttachedFile']),
    precision() {
      if (this.localExpense && this.localExpense.expenseType && this.localExpense.expenseType.unit === 'Km') {
        return 1;
      }
      return 2;
    },
    expenseTitle() {
      let title = this.$t('TIME_ENTRIES.EXPENSE.PAGE_LABEL_EDIT');
      if (this.isAdd) {
        title = this.$t('TIME_ENTRIES.EXPENSE.PAGE_LABEL_ADD');
      } else if (this.selectedTimeEntry.isSubmitted) {
        title = this.$t('TIME_ENTRIES.EXPENSE.PAGE_LABEL');
      }
      return title;
    },
    isAdd() {
      return this.$route.params.expenseId === 'add';
    },
    selectedAttachedFileUrl() {
      return (this.selectedAttachedFile
        ? `data:${this.selectedAttachedFile.contentType};base64,${this.selectedAttachedFile.file}`
        : ''
      );
    },
  },
  watch: {
    selectedExpense: {
      handler(newVal) {
        if (newVal) {
          this.localExpense = cloneDeep(newVal);
        } else {
          this.localExpense = {
            qty: 0.0,
            expenseType: {
              unit: '$',
            },
            attachedFiles: [],
          };
        }
      },
      immediate: true,
    },
  },
  created() {
    this.$store.dispatch('expense/resetExpense');
    this.$store.dispatch('attachedFile/resetAttachedFile');
    this.$store.dispatch('expense/expenseTypes').then(() => {
      if (!this.isAdd) {
        this.$store.dispatch('timeEntry/timeEntryById', this.$route.params.id);
        this.$store.dispatch('expense/expense', {
          timeEntryId: this.$route.params.id,
          expenseId: this.$route.params.expenseId,
        }).then(() => {
          if (this.localExpense.attachedFiles && this.localExpense.attachedFiles.length > 0) {
            this.$store.dispatch(
              'attachedFile/attachedFile',
              this.localExpense.attachedFiles[0].id,
            );
          }
        });
      }
    });
  },
  methods: {
    formatToOptions(array, textAttribute, valueAttribute) {
      if (!array) {
        return [];
      }
      return array.map(item => ({
        text: this.$t(item[textAttribute]),
        value: parseInt(item[valueAttribute], 10),
      }));
    },
    input(value) {
      if (!value || Number.isNaN(value)) {
        this.localExpense.expenseType.id = null;
        this.localExpense.expenseType.unit = '$';
      } else {
        this.localExpense.expenseType.id = value;
        this.localExpense.expenseType.unit = this.availableExpenses.find(
          expense => expense.id === value,
        ).unit;
      }
    },
    goToPhoto(id) {
      this.$router.push(
        `/dashboard/time-entries/${
          this.$route.params.id
        }/expense/${
          this.$route.params.expenseId
        }/photo/${
          id}`,
      );
    },
    edit() {
      this.$validator.validateAll().then(() => {
        if (this.$validator.errors.items.length > 0) {
          this.$store.commit('alerts/setAlert', {
            msg: this.$t(
              `VALIDATION.${
                this.$validator.errors.items[0].rule.toUpperCase()}`,
              [this.$validator.errors.items[0].field],
            ),
          });
        } else {
          this.$store.dispatch('expense/saveExpense', {
            data: this.localExpense,
            timeEntryId: this.$route.params.id,
          });
        }
      });
    },
    add() {
      this.$validator.validateAll().then(() => {
        if (this.$validator.errors.items.length > 0) {
          this.$store.commit('alerts/setAlert', {
            msg: this.$t(
              `VALIDATION.${
                this.$validator.errors.items[0].rule.toUpperCase()}`,
              [this.$validator.errors.items[0].field],
            ),
          });
        } else {
          this.$store.dispatch('expense/addExpense', {
            data: this.localExpense,
            timeEntryId: this.$route.params.id,
          });
        }
      });
    },
    confirmDelete() {
      this.$store.dispatch('expense/deleteExpense', {
        timeEntryId: this.$route.params.id,
        expenseId: this.$route.params.expenseId,
      });
    },
  },
};
</script>

<style scoped lang="scss">
div {
  &.photo-wrapper {
    border: 1px solid #000;
    border-radius: 10px;
    border-style: dashed;
  }

  &.time-entries {
    height: 100%;
  }
  &.content {
    padding-top: $top-menu-height;
    padding-bottom: $bottom-menu-height;
    background-color: $base;
    min-height: 100%;
  }
  &.fixed-week-selector {
    position: fixed;
    width: 100%;
    z-index: 1001;
    left: 0;
    top: $top-menu-height;
    background-color: $base;
    border-bottom: 2px solid $secondary;
  }
  &.actions {
    text-align: right;
  }
}

h4 {
  text-align: left;
  margin-bottom: 0px;
  text-transform: uppercase;
  font-weight: bold;
}
</style>
